import {Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch} from '@stencil/core';
import {CaptchaBox} from './src/captcha-box';
import {Status} from './src/status';
import {getTranslation, NamedTranslation} from "./locale";
import {License} from "./src/license";
import {ErrorCode} from "./src/api/error/error-code";
import {ErrorModel} from "./src/api/error/error-model";
import {CheckIcon, ErrorIcon, LogoIcon, PrivacyText, SpinnerIcon, TrustcaptchaText} from "./icons";
import {Config} from "./config";
import {Design, defaultDesign, mergeDesign} from "./default-design";

const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

@Component({
  tag: 'trustcaptcha-component',
  styleUrl: 'trustcaptcha-component.css',
  shadow: {delegatesFocus: true},
})
export class TrustcaptchaComponent {

  @Element()
  private hostElement: HTMLElement;

  @Prop()
  public sitekey: string;
  @Prop()
  public privacyUrl: string;
  @Prop()
  public apiUrl: string = 'https://api.trustcomponent.com';
  @Prop()
  public framework: string | null = null;
  @Prop()
  public language: string = 'auto';
  @Prop()
  public translations: string = '';
  @Prop()
  public theme: 'light' | 'dark' | 'media' = 'light';
  @Prop()
  public design: string = '';
  @Prop({reflect: true})
  public autostartDisabled: boolean = false;
  @Prop()
  public tokenFieldName: string = 'tc-verification-token';
  @Prop()
  public bypassToken: string | null = '';
  @Prop()
  public minimalDataMode: boolean = false;
  @Prop({reflect: true})
  public fullWidth: boolean = false;
  @Prop()
  public licenseKey: string | null = null;
  @Prop({reflect: true})
  public invisible: boolean = false;
  @Prop()
  public invisibleHint: 'hidden' | 'inline' | 'right-border' | 'right-bottom' = 'right-border';
  @Prop({reflect: true})
  public whiteLabel: boolean = false;
  @Prop({reflect: true})
  public failoverEnabled: boolean = false;

  @Event()
  public captchaStarted: EventEmitter<void>;
  @Event()
  public captchaSolved: EventEmitter<string>;
  @Event()
  public captchaFailed: EventEmitter<ErrorModel>;
  @Event()
  public captchaExpired: EventEmitter<void>;
  @Event()
  public captchaReset: EventEmitter<void>;

  @State()
  public captchaStatus = Status.START

  public captchaBox!: CaptchaBox;
  @State()
  public licenseObject?: License;
  @State()
  public translation: NamedTranslation;
  @State()
  public currentTheme: 'light' | 'dark' = 'light'
  @State()
  public failureText: string = ''
  @State()
  public mergedDesign: Design = defaultDesign;
  @State()
  public expiredAnnouncement: boolean = false;
  private expiredAnnouncementTimer?: number;
  private htmlLangObserver?: MutationObserver;
  private mediaThemeQuery?: MediaQueryList;
  private mediaThemeListener?: (event: MediaQueryListEvent) => void;


  private getConfig(): Config {
    return {
      siteKey: this.sitekey,
      privacyUrl: this.privacyUrl,
      apiUrl: this.apiUrl,
      framework: this.framework,
      language: this.language,
      theme: this.theme,
      currentTheme: this.currentTheme,
      autostartDisabled: this.autostartDisabled,
      tokenFieldName: this.tokenFieldName,
      bypassToken: this.bypassToken,
      minimalDataMode: this.minimalDataMode,
      fullWidth: this.fullWidth,
      translation: this.translation,
      whiteLabel: this.whiteLabel,
      invisible: this.invisible,
      failoverEnabled: this.failoverEnabled,
      licenseObject: this.licenseObject,
    };
  }

  async componentWillLoad(): Promise<void> {

    this.translation = getTranslation(this.language, this.translations);
    this.watchThemeUpdate()
    this.watchHtmlLangUpdate()

    this.captchaBox = new CaptchaBox(this.getConfig(), this.hostElement);
    this.captchaBox.statusChangedEvent.on('statusChanged', value => {
      this.captchaStatus = value;
      if (value == Status.RUNNING) {
        this.captchaStarted.emit();
      }
    });
    this.captchaBox.captchaFinishEvent.on("captchaSolved", () => {
      this.captchaSolved.emit(this.captchaBox.verificationToken)
    })
    this.captchaBox.captchaFinishEvent.on('captchaFailed', reason => {
      this.failureText = reason.message;
      this.captchaFailed.emit(reason)
    })
    this.captchaBox.captchaFinishEvent.on('captchaReset', () => {
      this.captchaReset.emit()
    })
    this.captchaBox.captchaFinishEvent.on('captchaExpired', () => {
      this.captchaExpired.emit();
      this.expiredAnnouncement = true;
      if (this.expiredAnnouncementTimer) {
        window.clearTimeout(this.expiredAnnouncementTimer);
      }
      this.expiredAnnouncementTimer = window.setTimeout(() => {
        this.expiredAnnouncement = false;
      }, 5000);
    })

    if (!this.validateSiteKey()) {
      this.recomputeMergedDesign();
      this.captchaBox.setup();
      return;
    }

    if (this.licenseKey !== null && this.licenseKey !== '') {
      this.licenseObject = await License.create(this.licenseKey, this.sitekey, errorModel => {
        this.captchaBox.setErrorStatus(errorModel);
      });
      if (!this.licenseObject) {
        this.recomputeMergedDesign();
        this.captchaBox.setup();
        return;
      }
    }

    this.validateLicensedFeatures();

    this.recomputeMergedDesign();

    this.captchaBox.setup();
  }

  private validateSiteKey(): boolean {
    if (!this.sitekey) {
      this.captchaBox.setErrorStatus(new ErrorModel(ErrorCode.NO_SITE_KEY_SPECIFIED, "No site key was provided."));
      return false;
    }
    if (!UUID_PATTERN.test(this.sitekey)) {
      this.captchaBox.setErrorStatus(new ErrorModel(ErrorCode.SITE_KEY_FORMAT_INVALID, "The provided site key is not a valid UUID."));
      return false;
    }
    return true;
  }

  private validateLicensedFeatures(): void {
    if (this.whiteLabel && !this.licenseObject?.whiteLabelOption) {
      this.captchaBox.setErrorStatus(new ErrorModel(ErrorCode.LICENSE_FEATURE_MISSING_WHITE_LABEL, "The white-label option is not included in your license."));
      return;
    }
    if (this.invisible && !this.licenseObject?.invisibilityOption) {
      this.captchaBox.setErrorStatus(new ErrorModel(ErrorCode.LICENSE_FEATURE_MISSING_INVISIBILITY, "The invisible option is not included in your license."));
      return;
    }
    if (this.privacyUrl && !this.licenseObject?.customizabilityOption) {
      this.captchaBox.setErrorStatus(new ErrorModel(ErrorCode.LICENSE_FEATURE_MISSING_CUSTOMIZABILITY, "The customizability option is not included in your license."));
      return;
    }
  }

  private get isBranded(): boolean {
    return !this.whiteLabel || !this.licenseObject?.whiteLabelOption;
  }

  private get hasCustomPrivacy(): boolean {
    return Boolean(this.privacyUrl) && Boolean(this.licenseObject?.customizabilityOption);
  }

  private recomputeMergedDesign(): void {
    if (this.design && this.licenseObject?.customizabilityOption) {
      try {
        this.mergedDesign = mergeDesign(JSON.parse(this.design));
        return;
      } catch (e) {
        console.error("Failed to parse custom design information");
      }
    }
    this.mergedDesign = defaultDesign;
  }

  @Watch('language')
  watchLanguageUpdate() {
    this.translation = getTranslation(this.language, this.translations);
    this.setNewConfig();
  }

  @Watch('theme')
  watchThemeUpdate() {
    this.detachMediaThemeListener();

    switch (this.theme) {
      case "media":
        if (window.matchMedia) {
          this.mediaThemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
          this.currentTheme = this.mediaThemeQuery.matches ? 'dark' : 'light';
          this.mediaThemeListener = (event: MediaQueryListEvent) => {
            this.currentTheme = event.matches ? 'dark' : 'light';
            this.setNewConfig();
          };
          // addEventListener is the modern API; older Safari versions only have addListener.
          if (typeof this.mediaThemeQuery.addEventListener === 'function') {
            this.mediaThemeQuery.addEventListener('change', this.mediaThemeListener);
          } else if (typeof (this.mediaThemeQuery as any).addListener === 'function') {
            (this.mediaThemeQuery as any).addListener(this.mediaThemeListener);
          }
        } else {
          this.currentTheme = 'light';
        }
        break;
      case "dark":
        this.currentTheme = 'dark';
        break;
      case "light":
      default:
        this.currentTheme = 'light';
        break;
    }

    this.setNewConfig();
  }

  private detachMediaThemeListener(): void {
    if (this.mediaThemeQuery && this.mediaThemeListener) {
      if (typeof this.mediaThemeQuery.removeEventListener === 'function') {
        this.mediaThemeQuery.removeEventListener('change', this.mediaThemeListener);
      } else if (typeof (this.mediaThemeQuery as any).removeListener === 'function') {
        (this.mediaThemeQuery as any).removeListener(this.mediaThemeListener);
      }
    }
    this.mediaThemeQuery = undefined;
    this.mediaThemeListener = undefined;
  }

  @Watch('translations')
  watchTranslationsUpdate() {
    this.translation = getTranslation(this.language, this.translations);
    this.setNewConfig();
  }

  @Watch('design')
  watchDesignUpdate() {
    this.recomputeMergedDesign();
  }

  private setNewConfig(): void {
    const newConfig = this.getConfig();
    if (this.captchaBox) {
      this.captchaBox.setNewConfig(newConfig);
    }
  }

  private watchHtmlLangUpdate(): void {
    this.htmlLangObserver = new MutationObserver(mutations => {
      for (let mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'lang' && this.language == 'auto') {
          this.translation = getTranslation('auto', this.translations);
          this.setNewConfig();
        }
      }
    });
    const config = {attributes: true, attributeFilter: ['lang']};
    this.htmlLangObserver.observe(document.documentElement, config);
  }

  @Method()
  public async startVerification() {
    this.captchaBox.startVerification();
  }

  @Method()
  public async reset() {
    this.captchaBox.reset();
  }

  disconnectedCallback() {
    if (this.expiredAnnouncementTimer) {
      window.clearTimeout(this.expiredAnnouncementTimer);
    }
    this.htmlLangObserver?.disconnect();
    this.htmlLangObserver = undefined;
    this.detachMediaThemeListener();
    this.captchaBox?.destroy();
  }


  render() {
    return (
      <Host style={{
        '--round-box': this.mergedDesign.rounding.box,
        '--round-cb': this.mergedDesign.rounding.checkbox,
        '--round-ih': this.mergedDesign.rounding.invisibleHint,
        '--b-bw': this.mergedDesign.borderWidth.box,
        '--cb-bw': this.mergedDesign.borderWidth.checkbox,
        '--b-default-bg-l': this.mergedDesign.theme.light.boxDefaultBackground,
        '--b-default-text-l': this.mergedDesign.theme.light.boxDefaultText,
        '--b-default-border-l': this.mergedDesign.theme.light.boxDefaultBorder,
        '--b-default-bg-d': this.mergedDesign.theme.dark.boxDefaultBackground,
        '--b-default-border-d': this.mergedDesign.theme.dark.boxDefaultBorder,
        '--b-default-text-d': this.mergedDesign.theme.dark.boxDefaultText,
        '--b-checkbox-bg-l': this.mergedDesign.theme.light.boxCheckboxBackground,
        '--b-checkbox-border-l': this.mergedDesign.theme.light.boxCheckboxBorder,
        '--b-checkbox-bg-d': this.mergedDesign.theme.dark.boxCheckboxBackground,
        '--b-checkbox-border-d': this.mergedDesign.theme.dark.boxCheckboxBorder,
        '--b-success-bg-l': this.mergedDesign.theme.light.boxSuccessBackground,
        '--b-success-border-l': this.mergedDesign.theme.light.boxSuccessBorder,
        '--b-success-bg-d': this.mergedDesign.theme.dark.boxSuccessBackground,
        '--b-success-border-d': this.mergedDesign.theme.dark.boxSuccessBorder,
        '--b-failure-bg-l': this.mergedDesign.theme.light.boxFailureBackground,
        '--b-failure-border-l': this.mergedDesign.theme.light.boxFailureBorder,
        '--b-failure-text-l': this.mergedDesign.theme.light.boxFailureText,
        '--b-failure-bg-d': this.mergedDesign.theme.dark.boxFailureBackground,
        '--b-failure-border-d': this.mergedDesign.theme.dark.boxFailureBorder,
        '--b-failure-text-d': this.mergedDesign.theme.dark.boxFailureText,
        '--ih-bg-l': this.mergedDesign.theme.light.invisibleHintBackground,
        '--ih-bg-d': this.mergedDesign.theme.dark.invisibleHintBackground,
        '--sp-color': this.currentTheme === 'light'
          ? this.mergedDesign.theme.light.spinnerColor
          : this.mergedDesign.theme.dark.spinnerColor,
      }}>
        {this.renderCheckboxDesign()}
        {this.invisible && this.invisibleHint !== 'hidden' && (
          <div>
            {this.invisibleHint === 'right-bottom' && (
              <div class="absolute bottom-5 right-5 z-[1000]">
                {this.renderInvisibleHint()}
              </div>
            )}
            {this.invisibleHint === 'right-border' && (
              <div class="absolute right-5 transform -translate-y-full z-[1000]">
                {this.renderInvisibleHint()}
              </div>
            )}
            {this.invisibleHint === 'inline' && (
              <div class="relative">
                <div class="absolute -right-2 transform translate-x-full -translate-y-full z-[1000]">
                  {this.renderInvisibleHint()}
                </div>
              </div>
            )}
          </div>
        )}
      </Host>
    );
  }

  private handleActivate = () => {
    if (this.captchaStatus === Status.START) {
      this.startVerification();
    } else if (this.captchaStatus === Status.FAILED) {
      this.reset();
    }
  }

  private isFromInteractiveChild = (event: Event): boolean => {
    const target = event.target as HTMLElement | null;
    if (!target || target === event.currentTarget) return false;
    return !!target.closest('a, button, input, select, textarea');
  }

  private get failureAnnouncement(): string {
    return `${this.translation.srFailed ?? ''} ${this.translation.srFailureReasonPrefix ?? ''}: ${this.failureText}`.trim();
  }

  private renderCheckboxDesign() {
    const isInteractive = this.captchaStatus === Status.START || this.captchaStatus === Status.FAILED;
    const ariaLabel =
      this.captchaStatus === Status.START ? this.translation.ariaLabelStart :
      this.captchaStatus === Status.RUNNING ? this.translation.ariaLabelRunning :
      this.captchaStatus === Status.DONE ? this.translation.ariaLabelDone :
      this.captchaStatus === Status.FAILED ? this.failureAnnouncement :
      '';

    return (
      <div class={{
        'tc-checkbox-box p-2 border-solid': true,
        'hidden': this.invisible && this.licenseObject?.invisibilityOption,
        'max-w-96': !this.fullWidth,
        'max-w-full': this.fullWidth,
        'cursor-pointer': isInteractive,
      }} style={{
        borderWidth: 'var(--b-bw)',
        borderRadius: 'var(--round-box)',
        backgroundColor:
          this.captchaStatus === Status.START || this.captchaStatus === Status.RUNNING
            ? this.currentTheme === 'light'
              ? 'var(--b-default-bg-l)'
              : 'var(--b-default-bg-d)'
            : this.captchaStatus === Status.DONE
              ? this.currentTheme === 'light'
                ? 'var(--b-success-bg-l)'
                : 'var(--b-success-bg-d)'
              : this.captchaStatus === Status.FAILED
                ? this.currentTheme === 'light'
                  ? 'var(--b-failure-bg-l)'
                  : 'var(--b-failure-bg-d)'
                : '',
        borderColor:
          this.captchaStatus === Status.START || this.captchaStatus === Status.RUNNING
            ? this.currentTheme === 'light'
              ? 'var(--b-default-border-l)'
              : 'var(--b-default-border-d)'
            : this.captchaStatus === Status.DONE
              ? this.currentTheme === 'light'
                ? 'var(--b-success-border-l)'
                : 'var(--b-success-border-d)'
              : this.captchaStatus === Status.FAILED
                ? this.currentTheme === 'light'
                  ? 'var(--b-failure-border-l)'
                  : 'var(--b-failure-border-d)'
                : '',
      }}
        onClick={(e) => {
          if (this.isFromInteractiveChild(e)) return;
          this.handleActivate();
        }}>
        <div class='flex space-x-2' style={{
          color: this.captchaStatus === Status.FAILED
            ? this.currentTheme === 'light' ? 'var(--b-failure-text-l)' : 'var(--b-failure-text-d)'
            : this.currentTheme === 'light' ? 'var(--b-default-text-l)' : 'var(--b-default-text-d)',
        }}>
          <div class="flex justify-center items-center p-1">
            <div class='tc-checkbox-control w-8 h-8 items-center content-center'
              role="checkbox"
              aria-checked={this.captchaStatus === Status.DONE ? "true" : "false"}
              aria-busy={this.captchaStatus === Status.RUNNING ? "true" : "false"}
              aria-disabled={!isInteractive ? "true" : "false"}
              tabindex="0"
              aria-label={ariaLabel}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  this.handleActivate();
                }
              }}>
              {this.captchaStatus == Status.START && this.renderCheckBoxIcon()}
              {this.captchaStatus == Status.RUNNING && <SpinnerIcon size={32} stroke={4}></SpinnerIcon>}
              {this.captchaStatus == Status.DONE && <CheckIcon size={32} color={this.currentTheme === 'light' ? this.mergedDesign.theme.light.successIconColor : this.mergedDesign.theme.dark.successIconColor}></CheckIcon>}
              {this.captchaStatus == Status.FAILED && <ErrorIcon size={32}></ErrorIcon>}
            </div>
          </div>
          <div class='grow content-center text-sm font-medium'>
            {this.captchaStatus == Status.START && this.translation.boxStart}
            {this.captchaStatus == Status.RUNNING && this.translation.boxInProgress}
            {this.captchaStatus == Status.DONE && this.translation.boxCompleted}
            {this.captchaStatus == Status.FAILED &&
              <div class='leading-3'>
                <span class="text-xs" style={{
                  color: this.currentTheme === 'light' ? 'var(--b-failure-text-l)' : 'var(--b-failure-text-d)',
                }}>{this.failureText}</span>
              </div>
            }
          </div>
          <div class="sr-only" aria-live="polite" aria-atomic="true">
            {this.expiredAnnouncement && this.translation.srExpired}
            {this.captchaStatus === Status.RUNNING && this.translation.srRunning}
            {this.captchaStatus === Status.DONE && this.translation.srDone}
            {this.captchaStatus === Status.FAILED && this.failureAnnouncement}
          </div>
          <div class='content-end'>
            <div class='flex flex-col justify-end space-x-2 text-xs'>
              {this.hasCustomPrivacy &&
                <PrivacyText privacyUrl={this.privacyUrl} translation={this.translation}></PrivacyText>
              }
              {this.isBranded &&
                <TrustcaptchaText currentTheme={this.currentTheme} translation={this.translation}></TrustcaptchaText>
              }
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderCheckBoxIcon() {
    return (
      <div class='h-8 w-8 p-2 border-solid' style={{
        borderWidth: 'var(--cb-bw)',
        borderRadius: 'var(--round-cb)',
        backgroundColor: this.currentTheme === 'light' ? 'var(--b-checkbox-bg-l)' : 'var(--b-checkbox-bg-d)',
        borderColor: this.currentTheme === 'light' ? 'var(--b-checkbox-border-l)' : 'var(--b-checkbox-border-d)',
      }}></div>
    )
  }

  private renderInvisibleHint() {
    return (
      <div class={{
        'p-3 rounded-md shadow-lg main-container flex gap-4': true,
        'hidden': this.captchaStatus === Status.START,
      }} style={{
        borderRadius: 'var(--round-ih)',
        color: this.captchaStatus === Status.FAILED
          ? this.currentTheme === 'light' ? 'var(--b-failure-text-l)' : 'var(--b-failure-text-d)'
          : this.currentTheme === 'light' ? 'var(--b-default-text-l)' : 'var(--b-default-text-d)',
        backgroundColor: this.captchaStatus === Status.FAILED
          ? this.currentTheme === 'light' ? 'var(--b-failure-bg-l)' : 'var(--b-failure-bg-d)'
          : this.currentTheme === 'light' ? 'var(--ih-bg-l)' : 'var(--ih-bg-d)',
      }}>
        <div class="flex justify-center items-center">
          <div class={{
            'iconLight': this.currentTheme == 'light',
            'iconDark': this.currentTheme == 'dark',
          }}>
            <LogoIcon size={32}></LogoIcon>
          </div>
          {this.captchaStatus == Status.RUNNING && <SpinnerIcon size={20} stroke={4}></SpinnerIcon>}
          {this.captchaStatus == Status.DONE && <CheckIcon size={20} color={this.currentTheme === 'light' ? this.mergedDesign.theme.light.successIconColor : this.mergedDesign.theme.dark.successIconColor}></CheckIcon>}
          {this.captchaStatus == Status.FAILED && <ErrorIcon size={20}></ErrorIcon>}
        </div>
        {(this.isBranded || this.hasCustomPrivacy) && (
          <div class='hidden-container content-center'>
            <div class='flex flex-col justify-end space-x-2 text-xs'>
              {this.isBranded &&
                <TrustcaptchaText currentTheme={this.currentTheme} translation={this.translation}></TrustcaptchaText>
              }
              {this.hasCustomPrivacy &&
                <PrivacyText privacyUrl={this.privacyUrl} translation={this.translation}></PrivacyText>
              }
            </div>
          </div>
        )}
      </div>
    )
  }
}

